<script setup>
import { computed, ref } from 'vue'
import UiModal from '@/components/ui/UiModal.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiField from '@/components/ui/UiField.vue'
import RegardingPicker from '@/components/form/RegardingPicker.vue'
import { ENTITIES } from '@/lib/entities.js'
import { entityLabel } from '@/lib/labels.js'
import { NOTE_ON } from '@/lib/notes.js'

/**
 * A note moved to another record (D-347): where it is and where it goes said side by side, sent
 * only on "Move" — and then, for a few seconds, still to be undone. Never with an edit.
 */
const props = defineProps({
  note: { type: Object, required: true },
})
const emit = defineEmits(['close', 'chosen'])
const target = ref(null)
const label = ref('')
const from = computed(() => props.note.noteable || null)
const same = computed(() => !!target.value && target.value.entity === from.value?.entity && target.value.uuid === from.value?.uuid)
function picked(record) {
  label.value = record?.label || ''
}
function confirm() {
  if (!target.value || same.value) return
  emit('chosen', { ...target.value, label: label.value })
}
</script>

<template>
  <UiModal open :title="$t(from ? 'notes.move.title' : 'notes.move.attachTitle')" size="md" @close="emit('close')">
    <div class="space-y-3" data-testid="note-move">
      <p class="text-sm text-muted">{{ $t('notes.move.intro') }}</p>
      <UiField :label="$t('notes.move.now')">
        <p v-if="from" class="flex items-center gap-2 text-sm"><i :class="['fa-solid', ENTITIES[from.entity]?.icon, ENTITIES[from.entity]?.color]" aria-hidden="true"></i><span v-private>{{ from.label }}</span><span class="text-xs text-faint">{{ entityLabel(from.entity) }}</span></p>
        <p v-else class="text-sm text-muted">{{ $t('notes.move.quick') }}</p>
      </UiField>
      <UiField :label="$t('notes.move.to')">
        <RegardingPicker v-model="target" :entities="NOTE_ON" @select="picked" />
      </UiField>
      <p v-if="same" class="text-sm text-amber-700 dark:text-amber-300">{{ $t('notes.move.same') }}</p>
      <!-- what happens, read before it does -->
      <p v-else-if="target" class="rounded-lg border border-brand-200 bg-brand-50/50 p-2 text-sm dark:border-brand-800 dark:bg-brand-900/20" data-testid="note-move-summary">
        <span v-private>{{ from?.label || $t('notes.move.quick') }}</span>
        <i class="fa-solid fa-arrow-right mx-2 text-xs text-brand-600" aria-hidden="true"></i>
        <strong v-private>{{ label || '…' }}</strong>
      </p>
    </div>
    <template #footer>
      <UiButton @click="emit('close')">{{ $t('common.cancel') }}</UiButton>
      <UiButton variant="primary" icon="fa-right-left" :disabled="!target || same" data-testid="note-move-confirm" @click="confirm">{{ $t(from ? 'notes.move.confirm' : 'notes.move.attach') }}</UiButton>
    </template>
  </UiModal>
</template>
